"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const Box_1 = tslib_1.__importDefault(require("@mui/material/Box"));
const styles_1 = require("@mui/material/styles");
const mui5_1 = require("@mnemonic/mui5");
/**
 * A wrapper around the filter box in @mnemonic/mui5 to make implementation easier and more consistent while ensure backwards compatibility.
 *
 * @example ### Advanced example
 *
 * This example shows a advanced implementation of a filter box containing several form fields.
 *
 * ```jsx live noInline
 * file://./__examples__/2_AdvancedFilterCustomer.tsx
 * ```
 *
 *
 * @param value The value, e. g mapped from a criteria. This should include values for all the fields used in the Filter.
 * @param label The label to be displayed when the filter is closed
 * @param count The count to display (is displayed when it is higher than countLimit)
 * @param countLimit When to go from displaying the return value of getOptionLabel to displaying the count (default = 2)
 * @param error If true - the filter turns red (should be used when an error is displayed)
 * @param minWidth The minimum width of the filter content when open (default="350px")
 * @param getOptionLabel A callback that enables you to choose what to display when count is under the countLimit
 * @param renderContent A callback that exposes "internalValue" and "setInternalValue" to the form fields. "internalValue" will automatically be updated when value changes.
 * @param onApply What happens when user press "Enter" or click "Apply"
 * @param closeText Prop for overriding the close button text
 * @param applyText Prop for overriding the apply button text
 * @returns A complete filter component used to filter a search
 * @category component
 */
const Filter = (props) => {
    const { closeText, applyText, value, label, count, countLimit = 2, error = false, minWidth = "350px", getOptionLabel, renderContent, onApply, ...rest } = (0, styles_1.useThemeProps)({ props, name: "Filter" });
    const [internalValue, setInternalValue] = (0, react_1.useState)(value);
    const [anchorEl, setAnchorEl] = (0, react_1.useState)(null);
    const onOpen = (evt) => {
        setAnchorEl(evt.currentTarget);
    };
    const onClose = () => {
        setAnchorEl(null);
        setInternalValue(value);
    };
    const onApplyAndClose = () => {
        onApply(internalValue);
        onClose();
    };
    /* Updating internalValue when value updates */
    (0, react_1.useEffect)(() => {
        setInternalValue(value);
    }, [value]);
    return (react_1.default.createElement(mui5_1.FilterBox, { fullWidth: true, disableRestoreFocus: true, ButtonComponentProps: { sx: { whiteSpace: "nowrap" } }, anchorEl: anchorEl, anchorOrigin: { horizontal: "left", vertical: "bottom" }, label: label, LabelComponentProps: {
            active: count > 0,
            badge: count >= countLimit ? count : undefined,
            primary: label,
            secondary: count === 1 ? getOptionLabel(value) : undefined,
        }, onKeyDown: (evt) => {
            /* evt.defaultPrevented is true, e. g when the user has selected an option in an autocompleter */
            if (evt.key === "Enter" && !evt.defaultPrevented) {
                onApplyAndClose();
            }
        }, onClick: onOpen, onClose: onClose, open: Boolean(anchorEl), state: error ? "error" : count > 0 ? "active" : "normal", ...rest },
        react_1.default.createElement(Box_1.default, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth,
            } }, renderContent({ internalValue, setInternalValue })),
        react_1.default.createElement(mui5_1.FilterBoxActions, { disablePadding: true },
            react_1.default.createElement(mui5_1.ButtonSecondaryAction, { onClick: onClose }, closeText),
            react_1.default.createElement(mui5_1.ButtonSecondaryAction, { onClick: onApplyAndClose }, applyText))));
};
exports.default = Filter;
